import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

import { DataGridTable, FormPage, GridRowAction } from "../../../components-old/ComponentsIndex";
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { useFetchListData } from "../../../hooks/useFetchListData";
import { Box } from "@mui/material";
import SwapHorizonOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { PricingPortalAPI } from "../../../apis/api-index";
import { useNavigate, createSearchParams } from "react-router-dom";

const AuditListForm = () => {
    /**************************************************
    //Form handlers & hooks
    **************************************************/
    const { t } = useTranslation();
    const spinnerCtx = useContext(DisplaySpinnerContext);
    const settingsConfig = useSelector(selectSessionConfig);

    const navigate = useNavigate();
    /**************************************************
    //Form State variables
    **************************************************/
    const [auditInfoRecordList, setAuditInfoRecordList] = useState([]);
    const [dataGridPageSize, setDataGridPageSize] = useState(10);
    const [gridPageSizeOptions] = useState([10, 20, 50]);
    const [totalAuditInfoRowCount, setTotalAuditInfoRowCount] = useState(0);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [selectedFromDate, setSelectedFromDate] = useState(null);
    const [selectedToDate, setSelectedToDate] = useState(null);

    const AuditInfoList = useFetchListData(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getAuditInfoList",
        "auditinfovantageportal",
        {required: true, clause: "&status=APPROVED"}
    );

    /**************************************************
    //Event definitions
    **************************************************/

    //Data grid Page Size Change event
    const handlePageSizeChange = (newPageSize) => {
        setDataGridPageSize(newPageSize);
    };

    //show audit modal
    const handleShowSelectedAuditDetails = (event) => {
        let _navigationData = { previousPath: "/auditinfo" };
        navigate(
            {
                pathname: "/reviewdetails",
                search: createSearchParams({
                    reviewFilter: `&auditid=${event.AuditId}`
                }).toString(),
            },
            { state: _navigationData }
        );
    };


    //clearing date filter
    const onClearFilter = () => {
        setSelectedFromDate(null);
        setSelectedToDate(null);
        setShowDateFilter(false);
        setAuditInfoList(AuditInfoList);
    };

    //applying date filter
    const onApplyDateFilter = async (fromDate, toDate) => {
        setSelectedFromDate(fromDate);
        setSelectedToDate(toDate);
        setShowDateFilter(false);
        let filterClause = `&status=APPROVED&fromDate=${fromDate}&toDate=${toDate}`;

        const filteredList = await PricingPortalAPI.getFilterList(
            settingsConfig.businessCode,
            settingsConfig.countryCode,
            "getAuditInfoList",
            "auditinfovantageportal",
            filterClause
        );
        setAuditInfoList(filteredList);
    };

    //setting data in list
    const setAuditInfoList = (recordlist) => {
        try {
            if (recordlist.length) recordlist.sort((a, b) => new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime());
            let _recordList = recordlist.map((item) => {
                return {
                    id: item.AuditId,
                    auditId: item.AuditId,
                    location: item.Location,
                    auditAction: item.Action,
                    userEmail: item.UserEmail?.split("@")[0],
                    modifiedBy: item.ModifiedBy?.split("@")[0],
                    createdDate: new Date(item.CreatedDate).toLocaleString("en-GB").replace(",", ""),
                    recordResponse: item,
                };
            });

            setAuditInfoRecordList(_recordList);
            setTotalAuditInfoRowCount(_recordList.length);
            spinnerCtx.setLoadingIndicator(false);
        } catch (err) {
            spinnerCtx.setLoadingIndicator(false);
            alert(err);
        }
    };
    /**************************************************
    //Form on load useEffect
    **************************************************/
    useEffect(() => {
        spinnerCtx.setLoadingIndicator(true);
        if (AuditInfoList) {
            setAuditInfoList(AuditInfoList);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AuditInfoList]);

    /**************************************************
    //JSX delcaration
    **************************************************/
    const buttonConfigs = [
        {
            buttonText: <FilterListIcon />,
            buttonType: "filled",
            onClick: () => {
                setShowDateFilter(!showDateFilter);
            },
            buttonColor: "#A62B23",
            textColor: "#fff",
        },
        {
            buttonText: <SystemUpdateAltIcon />,
            buttonType: "outlined",
            onClick: () => {
                AppUtility.handleDownload(auditInfoRecordList, "AuditInfo.xlsx");
            },
            buttonColor: "#A62B23",
            textColor: "#A62B23",
        },
    ];

    const dataGridSchema = [
        {
            field: "createdDate",
            headerName: t("auditInfoList.createdDate"),
            flex: 1,
        },
        {
            field: "userEmail",
            headerName: t("auditInfoList.changesMadeBy"),
            flex: 1,
        },
        {
            field: "modifiedBy",
            headerName: t("auditInfoList.approvedBy"),
            flex: 1,
        },
        {
            field: "auditAction",
            headerName: t("auditInfoList.action"),
            flex: 1,
        },
        {
            field: "location",
            headerName: t("auditInfoList.location"),
            flex: 1,
        },
        {
            field: "action",
            headerName: t("pricingChanges.changes"),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <>
                        <Box width={25} />
                        {
                            <>
                                <GridRowAction
                                    width={25}
                                    height={25}
                                    handleSubmit={() => handleShowSelectedAuditDetails(params.row.recordResponse)}
                                >
                                 <SwapHorizonOutlinedIcon />
                                </GridRowAction>
                            </>
                        }
                    </>
                );
            },
        },
        {
            field: "recordResponse",
            hide: true,
        },
    ];

    return (
        <FormPage isList={true}>
            <DataGridTable
                rows={auditInfoRecordList}
                columns={dataGridSchema}
                pageSize={dataGridPageSize}
                pageSizeOptions={gridPageSizeOptions}
                onPageSizeChange={handlePageSizeChange}
                totalRowCount={totalAuditInfoRowCount}
                toolbarHeader={t("auditInfoList.header")}
                toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
                buttonConfigs={buttonConfigs}
                showDateFilter={showDateFilter}
                onApplyDateFilter={onApplyDateFilter}
                onClearFilter={onClearFilter}
                selectedFromDate={selectedFromDate}
                selectedToDate={selectedToDate}
            />
        </FormPage>
    );
};

export default AuditListForm;
